import {ParamComponent} from 'panel';
import produce from 'immer';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {IronContextRegistry} from 'common/report/context-registry';
import {TemplateSpecs, TemplateType} from 'common/report/dashboards/types';

import css from './index.inline.styl';
import {template} from './template';
import {
  ECommerceTemplateFields,
  InstructionalTooltipConfig,
  RHSPanelParams,
  RHSPanelProps,
  RHSPanelState,
  TemplateField,
} from './types';

export class RHSPanel extends ParamComponent<RHSPanelParams, RHSPanelState, IronContextRegistry> {
  // START: copied from boards-template-modal
  readonly TEMPLATE_SELECTION_STEP = 0;
  readonly EVENT_SELECTION_STEP = 1;
  readonly WHAT_TO_TRACK_URL = `https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track`;
  readonly VALUE_MOMENT_FIELD_TOOLTIP_TEXT = `For a social media platform, it could be creating a post; for a streaming service, it's watching a video; for e-commerce, it might be completing a purchase. [Learn More](https://developer.mixpanel.com/docs/plan-your-implementation#what-to-track)`;

  hasRequiredFields = (requiredFields: TemplateField[]): boolean => {
    return requiredFields.every((field) => !!this.state.fields[field]);
  };

  readonly TEMPLATE_CONFIGS = Object.keys(TemplateSpecs).reduce((acc, key) => {
    const {requiredFields, ...rest} = TemplateSpecs[key];
    return {
      ...acc,
      [key]: {
        ...rest,
        requiredFieldsCheck: () => this.hasRequiredFields(requiredFields),
      },
    };
  }, {});

  ECOMMERCE_INSTRUCTIONAL_TOOLTIP_CONFIG: {[key in ECommerceTemplateFields]?: InstructionalTooltipConfig} = {
    [ECommerceTemplateFields.PURCHASE_EVENT]: {
      stepIndex: 1,
      title: `Select your Purchase event`,
      body: `This is the event that represents a completed customer order and payment transaction.`,
      shouldShow: () =>
        !this.state.fields[ECommerceTemplateFields.PURCHASE_EVENT] &&
        !this.elRefs[ECommerceTemplateFields.PURCHASE_EVENT]?.state.hasOpenDropdown,
    },
    [ECommerceTemplateFields.CART_PROPERTY]: {
      stepIndex: 2,
      title: `Select your Cart property`,
      body: `The Cart property is associated with your Purchase event and is a list of objects that represents the items a user has in their cart. \nThis property is often named Shopping cart, Cart, Bag, or something similar.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.PURCHASE_EVENT] &&
        !this.state.fields[ECommerceTemplateFields.CART_PROPERTY] &&
        !this.elRefs[ECommerceTemplateFields.CART_PROPERTY]?.state.isMenuOpen,
    },
    [ECommerceTemplateFields.PRICE_KEY]: {
      stepIndex: 3,
      title: `Select your Price property`,
      body: `The Price sub-property is associated with your Cart property and can be anything that represents how much an individual item costs.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.CART_PROPERTY] &&
        !this.state.fields[ECommerceTemplateFields.PRICE_KEY] &&
        !this.elRefs[ECommerceTemplateFields.PRICE_KEY]?.state.isMenuOpen,
    },
    [ECommerceTemplateFields.ADD_TO_CART_EVENT]: {
      stepIndex: 1,
      title: `Select your Add to Cart event`,
      body: `This is the event that represents a customer adding an item to their cart or shopping bag.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.PRICE_KEY] &&
        !this.state.fields[ECommerceTemplateFields.ADD_TO_CART_EVENT] &&
        !this.elRefs[ECommerceTemplateFields.ADD_TO_CART_EVENT]?.state.hasOpenDropdown,
    },
    [ECommerceTemplateFields.CART_PROPERTY_2]: {
      stepIndex: 2,
      title: `Select your Cart property`,
      body: `The Cart property is associated with your Purchase event and is a list of objects that represents the items a user has in their cart. \nThis property is often named Shopping cart, Cart, Bag, or something similar.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.ADD_TO_CART_EVENT] &&
        !this.state.fields[ECommerceTemplateFields.CART_PROPERTY_2] &&
        !this.elRefs[ECommerceTemplateFields.CART_PROPERTY_2]?.state.isMenuOpen,
    },
    [ECommerceTemplateFields.CATEGORY_KEY]: {
      stepIndex: 3,
      title: `Select your Category property`,
      body: `This sub-property is any descriptor about the objects in a user's cart such as Category, Brand, Item type, Style, etc. \nFor example, this property is used in a report that tells you what category has the most sales.`,
      shouldShow: () =>
        !!this.state.fields[ECommerceTemplateFields.CART_PROPERTY_2] &&
        !this.state.fields[ECommerceTemplateFields.CATEGORY_KEY] &&
        !this.elRefs[ECommerceTemplateFields.CATEGORY_KEY]?.state.isMenuOpen,
    },
  };

  elRefs = {};

  setElRef(ref, el) {
    this.elRefs[ref] = el;
  }
  // END: copied from boards-template-modal
  get config() {
    return {
      contexts: [`currentProject` as const],
      css,
      helpers: {
        getNoDataEventString: () => {
          switch (this.params.selectedTemplate) {
            case TemplateType.KPI:
            case TemplateType.FEATURE_LAUNCH:
              return `just two events`;
            case TemplateType.LIFECYCLE_COHORT_ANALYSIS:
              return `just one event`;
            default:
              return `some events`;
          }
        },
        hasIntegratedArb: (): boolean | undefined => this.getContext(`currentProject`).get()?.value?.hasIntegratedArb,
        updateField: (value: any, eventName: TemplateField) => {
          this.update(
            produce(this.state, (draft) => {
              draft.fields[eventName] = value;
            }),
          );
        },
        removeField: (eventName: TemplateField) => {
          this.update(
            produce(this.state, (draft) => {
              delete draft.fields[eventName];
            }),
          );
        },
      },
      useShadowDom: true,
      params: {
        mode: String,
        selectedTemplate: String,
      },
      template,
    };
  }
}

export const RHSPanelEl = defineAndMakeJsxFactory<RHSPanelProps>(`mp-rhs-panel`, RHSPanel);
export const RHSPanelReact = panel2react<RHSPanelProps>(`mp-rhs-panel`, RHSPanel);
